/**
 * The file picker a generic-link visitor sees once their email is OTP-verified.
 *
 * Split out of `IdentifyScreen` when D-25's generic-link OTP step was added: that screen ends the
 * moment a code is requested, and everything below - the picker, the progress bar, the sent list -
 * belongs to what comes after verification, not before it.
 */
import { useRef, useState } from 'react';
import { Button, Card, Page, Progress, Row, StatusPill } from '@iqz-ddds/components';
import { FailureNotice } from '../components/FailureNotice';
import { formatBytes } from '../upload/tus';

export interface GenericUploadScreenProps {
  readonly repName?: string | undefined;
  readonly onUpload: (input: {
    readonly file: File;
    readonly onProgress?: (sent: number, total: number) => void;
  }) => Promise<void>;
}

export function GenericUploadScreen({ repName, onUpload }: GenericUploadScreenProps): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [progress, setProgress] = useState<{ sent: number; total: number; filename: string } | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<readonly string[]>([]);
  const picker = useRef<HTMLInputElement>(null);

  const handleFilesChosen = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    event.target.value = '';
    if (files.length === 0) return;

    setBusy(true);
    setError(null);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i]!;
        const filePrefix = files.length > 1 ? `(${i + 1}/${files.length}) ` : '';
        setProgress({ sent: 0, total: file.size, filename: `${filePrefix}${file.name}` });

        await onUpload({
          file,
          onProgress: (sent, total) => {
            setProgress({ sent, total, filename: `${filePrefix}${file.name}` });
          },
        });
        setUploadedFiles((prev) => [...prev, file.name]);
      }
      setProgress(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Upload failed.'));
      setProgress(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page eyebrow="Secure upload" title={repName ? `Send documents to ${repName}` : 'Send documents securely'}>
      <FailureNotice error={error} />
      <Card>
        <input ref={picker} type="file" multiple hidden onChange={(e) => void handleFilesChosen(e)} />
        <Button type="button" onClick={() => picker.current?.click()} disabled={busy}>
          {busy ? 'Uploading…' : 'Choose files to upload'}
        </Button>

        {progress !== null && (
          <div style={{ marginTop: '1rem' }}>
            <Progress
              value={progress.total > 0 ? progress.sent / progress.total : 0}
              label={`Uploading ${progress.filename} (${formatBytes(progress.sent)} of ${formatBytes(progress.total)})`}
            />
          </div>
        )}
      </Card>

      {uploadedFiles.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <h2>Uploaded documents</h2>
          <Card>
            <ul className="ddds-list">
              {uploadedFiles.map((name, index) => (
                <Row key={`${name}-${index}`} name={name} action={<StatusPill tone="done">Sent</StatusPill>} />
              ))}
            </ul>
          </Card>
          <p className="ddds-footer" style={{ marginTop: '0.75rem' }}>Your documents have been securely delivered.</p>
        </div>
      )}
    </Page>
  );
}
